import React, { useState, useEffect, useRef, useCallback } from 'react';
import TitleBarContainer from './TitleBarContainer';
import BottomPanel from './BottomPanel';
import Settings from '../../components/Settings/Settings';
import { fileSystemService } from '../../services/fileSystem';
import { FileItem, OpenTab } from '../../types/files';
import Editor from '../../components/Editor/Editor';
import SidebarContainer from '../SidebarContainer/SidebarContainer';
import { useCollab } from '../../contexts/CollabContext';
import * as Y from 'yjs';
import EditorTabs from '../../components/Editor/EditorTabs';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { useAuth } from '../../contexts/AuthContext';
import { Mode } from 'types/modes';
import { getLanguageFromExtension } from 'utils/LanguageDetector';
import { YjsSignalrConnector } from '../../impl/yjsSignalrConnector';
import { YjsSignalrConnectorE2EE } from '../../impl/yjsSignalrConnectorE2EE';
import { LoginModal } from '../../components/Auth/LoginModal';
import { RegisterModal } from '../../components/Auth/RegisterModal';
import { SenderKeyState, RemoteSenderKeyState } from '../../crypto/utils/yjsUpdateEncrypt';
import { fromBase64, toBase64 } from '../../crypto/utils/cryptoHelper';
import { assertRealPath } from 'utils/fileTree';
import { useYjsDocument } from 'contexts/YjsDocumentContext';
import { generateIdentityKeyPair } from '../../crypto/utils/cryptoEngine';

const EditorContainer: React.FC = () => {
  const {
    mode,
    activeTab,
    setActiveTab
  } = useGlobalContext();

  const collab = useCollab();
  const { user } = useAuth();

  const yjsDocument = useYjsDocument();

  const userIdRef = useRef<string | null>(null);
  const announcedSenderKeysRef = useRef<Set<string>>(new Set());
  const username = user?.name || '';

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  const announceSenderKeyForFile = useCallback(async (
    filePath: string,
    opts?: { force?: boolean; reason?: string }
  ) => {
    if (!collab.connection || !collab.sessionId || !userIdRef.current) return;

    const local = yjsDocument.keyStore.getLocalKey(collab.sessionId, filePath);
    if (!local || !local.rootChainKey || !local.ed25519PublicKey) {
      console.warn(`[SenderKey][Announce] Missing local key or rootChainKey for ${filePath}; reason=${opts?.reason ?? 'unknown'}`);
      return;
    }

    if (!opts?.force && announcedSenderKeysRef.current.has(filePath)) return;

    const dto = {
      sessionId: collab.sessionId,
      filePath,
      senderUserId: userIdRef.current,
      recipientUserId: '',
      senderKeyId: local.senderKeyId,
      nonce: '',
      encryptedSenderRootKey: toBase64(local.rootChainKey), // ✨ Send rootChainKey instead of current chainKey
      signature: toBase64(local.ed25519PublicKey),
      lastMessageIndex: local.nextMessageIndex - 1,
      x25519PublicKey: local.x25519PublicKey ? toBase64(local.x25519PublicKey) : undefined,
    };

    try {
      await collab.connection.invoke('AnnounceSenderKeyForFile', dto);
      announcedSenderKeysRef.current.add(filePath);
      console.log(`[SenderKey][Announce] Sent for ${filePath} | reason=${opts?.reason ?? 'n/a'}`);
    } catch (err) {
      console.warn('[SenderKey][Announce] Failed:', err);
    }
  }, [collab.connection, collab.sessionId, yjsDocument]);

  const requestSenderKeys = useCallback(async (filePath: string) => {
    if (!collab.connection || !collab.sessionId) {
      console.warn('⚠️ [RequestKeys] No connection or sessionId');
      return;
    }

    try {
      console.log(`📥 [RequestKeys] Backend'den key listesi isteniyor | file=${filePath}`);
      await collab.connection.invoke('GetSenderKeysForFile', collab.sessionId, filePath);
      console.log(`✅ [RequestKeys] İstek gönderildi`);
    } catch (err) {
      console.error('❌ [RequestKeys] Backende istek atılamadı:', err);
    }
  }, [collab.connection, collab.sessionId]);

  // // Cleanup when connection is lost or session ends
  // useEffect(() => {
  //   const conn = collab.connection;
  //   if (!conn) {
  //     // No connection, cleanup everything
  //     console.log('[EditorContainer] No connection, disposing all Y.Docs and connectors');
  //     yjsDocument.disposeAll();
  //     yDocsRef.current = {};
  //     joinedFilesRef.current.clear();
  //     yjsUpdateHandlerAddedRef.current.clear();
  //     baselineReadyRef.current.clear();
  //     return;
  //   }

  //   // Listen for connection close/disconnect
  //   const handleConnectionClosed = () => {
  //     console.log('[EditorContainer] Connection closed, disposing all Y.Docs and connectors');
  //     yjsDocument.disposeAll();
  //     yDocsRef.current = {};
  //     joinedFilesRef.current.clear();
  //     yjsUpdateHandlerAddedRef.current.clear();
  //     baselineReadyRef.current.clear();
  //   };

  //   conn.onclose(handleConnectionClosed);

  //   return () => {
  //     // Cleanup listener (though onclose replaces previous handlers)
  //   };
  // }, [collab.connection, yjsDocument]);

  
  // Cleanup all Y.Docs and connectors when session ends
  useEffect(() => {
    if (!collab.connection) {
      // Session ended, cleanup everything
      console.log('[EditorContainer] Session ended, disposing all Y.Docs and connectors');
      yjsDocument.disposeAll();
      yDocsRef.current = {};
      joinedFilesRef.current.clear();
      yjsUpdateHandlerAddedRef.current.clear();
      baselineReadyRef.current.clear();
    }
  }, [collab.connection, yjsDocument]);

  const [language, setLanguage] = useState('javascript');
  const [theme, setTheme] = useState('vs-dark');
  const [isBottomPanelVisible, setBottomPanelVisible] = useState(true);
  const [isSettingsVisible, setSettingsVisible] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [openTabs, setOpenTabs] = useState<OpenTab[]>([]);
  const [previewTab, setPreviewTab] = useState<OpenTab | null>(null);
  const yjsUpdateHandlerAddedRef = useRef<Set<string>>(new Set());
  const joinedFilesRef = useRef<Set<string>>(new Set());
  // Yjs doc yönetimi: filePath -> { ydoc, awareness, connector, snapshotReady }
  const yDocsRef = useRef<{ [filePath: string]: { ydoc: Y.Doc, awareness?: any, connector?: YjsSignalrConnector | YjsSignalrConnectorE2EE, snapshotReady?: boolean } }>({});
  // Yjs update handler aktif mi?
  const yjsUpdateHandlerActiveRef = useRef<{ [filePath: string]: boolean }>({});
  const dirtyStateRef = useRef<Record<string, boolean>>({});
  const baselineReadyRef = useRef<Set<string>>(new Set());
  const openTabsRef = useRef<OpenTab[]>([]);
  const previewTabRef = useRef<OpenTab | null>(null);
  const activeTabRef = useRef<OpenTab | null>(null);

  useEffect(() => {
    openTabsRef.current = openTabs;
  }, [openTabs]);

  useEffect(() => {
    previewTabRef.current = previewTab;
  }, [previewTab]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Aktif dosya için Y.Doc'u döndür
  const getYDocForFile = (filePath: string) => {
    if (!yDocsRef.current[filePath]) {
      const ydoc = yjsDocument.getOrCreateDoc(filePath, () => new Y.Doc());
      yDocsRef.current[filePath] = { ydoc };
    }
    return yDocsRef.current[filePath];
  };

  const getYDocContent = (filePath: string) => {
    const entry = yDocsRef.current[filePath];
    if (!entry?.ydoc) return '';
    return entry.ydoc.getText('monaco').toString();
  };

  const attachDirtyListener = (filePath: string, ydoc: Y.Doc) => {
    if (yjsUpdateHandlerAddedRef.current.has(filePath)) return;
    yjsUpdateHandlerAddedRef.current.add(filePath);

    ydoc.on('updateV2', () => {
      // Skip initial bootstrap until baseline is established
      if (!baselineReadyRef.current.has(filePath)) return;

      // Host is the authority to broadcast dirty; still mark local UI
      if (!dirtyStateRef.current[filePath]) {
        setTabDirty(filePath, true);

        if (mode === Mode.Host && collab.sessionId && collab.connection) {
          collab.connection.invoke('SetDocumentDirty', collab.sessionId, filePath, true)
            .catch(err => console.error('SetDocumentDirty failed:', err));
        }
      }
    });
  };

  const setTabDirty = (
    filePath: string,
    isDirty: boolean,
    options?: { updateOriginal?: boolean; originalContentOverride?: string }
  ) => {
    const updateOriginal = options?.updateOriginal ?? false;
    const nextOriginal = updateOriginal
      ? options?.originalContentOverride ?? getYDocContent(filePath)
      : undefined;

    // Avoid unnecessary state churn/rerenders if nothing changes
    if (!updateOriginal && dirtyStateRef.current[filePath] === isDirty) {
      return;
    }

    setOpenTabs(prev => prev.map(tab => {
      if (tab.file.path !== filePath) return tab;
      return {
        ...tab,
        isDirty,
        originalContent: updateOriginal ? nextOriginal ?? tab.originalContent : tab.originalContent,
        content: tab.content
      };
    }));

    setPreviewTab(prev => {
      if (!prev || prev.file.path !== filePath) return prev;
      return {
        ...prev,
        isDirty,
        originalContent: updateOriginal ? nextOriginal ?? prev.originalContent : prev.originalContent,
        content: prev.content
      };
    });

    setActiveTab(prev => {
      if (!prev || prev.file.path !== filePath) return prev;
      return {
        ...prev,
        isDirty,
        originalContent: updateOriginal ? nextOriginal ?? prev.originalContent : prev.originalContent,
        content: prev.content
      };
    });

    dirtyStateRef.current[filePath] = isDirty;
    if (!isDirty) {
      baselineReadyRef.current.add(filePath);
    }
  };

  // Dosyaya join/leave mantığı - FILE ROOM BASED
  const joinDocument = async (filePath: string) => {

    if (!(mode === Mode.Host || mode === Mode.Join) || !collab.connection) {
      console.error('❌ [EditorContainer] Cannot join: Invalid mode or no connection');
      return;
    }

    const conn = collab.connection!;

    
    // Check if we can reuse existing connector (preview->permanent or tab switch)
    const existingConn = yDocsRef.current[filePath]?.connector;
    const alreadyJoined = joinedFilesRef.current.has(filePath);
    
    if (existingConn && alreadyJoined) {
      console.log(`♻️  [EditorContainer] Reusing existing connector for: ${filePath}`);
      console.log('  - Sender key already announced earlier, skipping re-announcement...');
      await conn.invoke('SetActiveDocument', collab.sessionId, filePath, username);
      return;
    }

    



    // Dosya için Yjs dokümanı ve connector oluştur
    const { ydoc } = getYDocForFile(filePath);
    attachDirtyListener(filePath, ydoc);

    // 🎯 HOST: Read file content from disk on first open
    if (mode === Mode.Host) {
      const yText = ydoc.getText('monaco');
      
      // Always sync from disk when joining - file might have been modified externally
      // or host might have closed tab and reopened (Y.Doc still exists but needs refresh)
      console.log('  📂 [HOST] Syncing Y.Doc with disk...');
      console.log('    📁 Virtual Path:', filePath);

      // Convert virtual path to real path
      const realPath = collab.getRealPath(filePath);
      console.log('    🗂️  Real Path:', realPath);

      if (realPath) {
        try {
          const fileContent = await fileSystemService.readFile(realPath);
          console.log('    ✅ File content read from disk, length:', fileContent.length);
          
          // Clear existing content and insert fresh content
          if (yText.length > 0) {
            console.log('    🔄 Clearing old Y.Doc content (length:', yText.length, ')');
            yText.delete(0, yText.length);
          }
          
          console.log('    📝 Inserting fresh content into Y.Doc...');
          yText.insert(0, fileContent);
          console.log('    ✅ Y.Doc synchronized with disk');
          console.log('    📸 Snapshot ready for other users');
          yDocsRef.current[filePath].snapshotReady = true; // Host has content immediately
          baselineReadyRef.current.add(filePath);
          setTabDirty(filePath, false, { updateOriginal: true });
        } catch (err) {
          console.error('    ❌ Failed to read file from disk:', err);
        }
      } else {
        console.warn('    ⚠️  Could not resolve real path for:', filePath);
      }
    }

    // CRITICAL: Different behavior for Host vs Joiner
    const existingConnector = yDocsRef.current[filePath]?.connector;
    
    // 🔄 BOTH Host and Joiner: Reuse existing connector if available
    // This prevents duplicate join requests and preserves sender key chain
    if (existingConnector) {
      console.log(`  ♻️ [${mode === Mode.Host ? 'Host' : 'Joiner'}] Reusing existing connector`);
      // Just reconnect awareness - DON'T rebuild editor, it's already working!
      yDocsRef.current[filePath].awareness = existingConnector.awareness;
      
      // Backend'e join isteği gönder (idempotent) - only if not already joined
      if (!joinedFilesRef.current.has(filePath)) {
        await conn.invoke('JoinDocument', collab.sessionId, filePath, userIdRef.current, username);
        joinedFilesRef.current.add(filePath);
      } else {
        // Already joined, just update active document
        await conn.invoke('SetActiveDocument', collab.sessionId, filePath, userIdRef.current, username);
      }
      
      console.log(`✅✅✅ [FileRoom] ${mode === Mode.Host ? 'HOST' : 'JOINER'} REUSED EXISTING CONNECTOR`);
      console.log('════════════════════════════════════════════════════════════');
      return; // Don't create new connector!
    }

    // Create new connector (Joiner always, Host only if doesn't exist)
    const shouldCreateFreshConnector = !yDocsRef.current[filePath]?.connector;
    
    if (shouldCreateFreshConnector) {
      console.log(`  🔧 [${mode === Mode.Host ? 'Host' : 'Joiner'}] Creating NEW connector...`);

      const currentUserColor =
        collab.users.find(u => u.name === username)?.color || '#3B82F6';

      // Joiner starts with snapshotReady=false until snapshot arrives
      if (mode === Mode.Join) {
        yDocsRef.current[filePath].snapshotReady = false;
      }

      // Fresh ephemeral key per room (per join)
      const identity = await generateIdentityKeyPair();
      const ensuredLocal = yjsDocument.keyStore.ensureLocalKey(
        collab.sessionId!,
        filePath,
        userIdRef.current!,
        identity.ed25519PrivateKey,
        {
          ed25519PublicKey: identity.ed25519PublicKey,
          x25519PrivateKey: identity.x25519PrivateKey,
          x25519PublicKey: identity.x25519PublicKey,
        }
      );

      const connector = yjsDocument.getOrCreateConnector(filePath, () => new YjsSignalrConnectorE2EE(
        ydoc,
        conn,
        collab.sessionId!,
        filePath,
        username,
        {
          userId: userIdRef.current!,
          userColor: currentUserColor,
          ed25519PrivateKey: ensuredLocal.ed25519PrivateKey,
          keyStore: yjsDocument.keyStore,
          isHost: mode === Mode.Host,
          onSnapshotReceived: () => {
            yDocsRef.current[filePath].snapshotReady = true;
            rebuildEditorInstance(filePath);
          },
          // ✨ Request sender keys callback when decrypt fails
          onRequestSenderKeys: async (file: string) => {
            await requestSenderKeys(file);
          }
        }
      ));

      yDocsRef.current[filePath].connector = connector;
      yDocsRef.current[filePath].awareness = connector.awareness;

      rebuildEditorInstance(filePath);
    } else {
      console.log('  ♻️  Connector already exists for this file');
    }

    // ✨ CRITICAL FIX: Join the group FIRST, then announce sender key
    // This ensures we receive all broadcasted keys from other users
    await conn.invoke('JoinDocument', collab.sessionId, filePath, userIdRef.current, username);
    joinedFilesRef.current.add(filePath);
    
    // Announce my sender key for this file so host/peers can decrypt my updates
    // Backend will broadcast ALL keys (including ours) to everyone in the group
    await announceSenderKeyForFile(filePath, { reason: 'initial-join' });

    console.log('✅✅✅ [FileRoom] SUCCESSFULLY JOINED FILE ROOM: ' + filePath);
    console.log('════════════════════════════════════════════════════════════');
  };

  // Her tab için editor ve binding yönetimi
  const [, forceEditorsRerender] = useState(0);

  const editorInstancesRef = useRef<{
    [filePath: string]: {
      editor: React.ReactNode;
      ref: React.RefObject<any>;
      tab: OpenTab;
    }
  }>({});

  const ensureAwarenessLocalState = (filePath: string) => {
    const docData = yDocsRef.current[filePath];
    if (!docData?.awareness) return;
    const currentUserColor =
      collab.users.find(u => u.name === username)?.color || '#3B82F6';
    // Restore presence when tab is (re)opened
    docData.awareness.setLocalState({ name: username, color: currentUserColor });
  };

  // Tab açıldığında editor instance oluştur
  const ensureEditorInstance = (tab: OpenTab) => {
    const filePath = tab.file.path;
    console.log('🏗️ [ensureEditorInstance] Called for file:', filePath);
    const existing = editorInstancesRef.current[filePath];
    if (existing) {
      console.log('✅ [ensureEditorInstance] Using existing instance for:', filePath);
      ensureAwarenessLocalState(filePath);
      existing.tab = tab;
      return;
    }

    console.log('🆕 [ensureEditorInstance] Creating NEW instance for:', filePath);
    const { editor, ref } = createEditorElement(tab);
    editorInstancesRef.current[filePath] = {
      editor,
      ref,
      tab
    };
    console.log('💾 [ensureEditorInstance] Saved instance to ref, forcing rerender');
    forceEditorsRerender(prev => prev + 1);
  };

  // Aktif tab değiştikçe, ilgili editor instance'ı oluştur
  useEffect(() => {
    if (activeTab) {
      ensureEditorInstance(activeTab);
      rebuildEditorInstance(activeTab.file.path);
    }
  }, [activeTab, theme, mode]);

  // Mode değiştiğinde tüm tabları temizle
  useEffect(() => {
    // Tüm açık tabları kapat
    setOpenTabs([]);
    setPreviewTab(null);
    // activeTab zaten GlobalContext'te leaveSession'da temizleniyor

    // Monaco editor instance'larını temizle (ref içindeki editor'ları dispose et)
    Object.keys(editorInstancesRef.current).forEach(filePath => {
      const instance = editorInstancesRef.current[filePath];
      if (instance?.ref?.current?.getEditor) {
        const editor = instance.ref.current.getEditor();
        if (editor && editor.dispose) {
          editor.dispose();
        }
      }
    });
    editorInstancesRef.current = {};

    // Yjs dokümanlari temizle
    Object.keys(yDocsRef.current).forEach(filePath => {
      const docData = yDocsRef.current[filePath];
      if (docData?.connector) {
        docData.connector.destroy();
      }
      if (docData?.ydoc) {
        docData.ydoc.destroy();
      }
    });
    yDocsRef.current = {};
    yjsUpdateHandlerActiveRef.current = {};
    yjsUpdateHandlerAddedRef.current.clear();
    joinedFilesRef.current = new Set();
    dirtyStateRef.current = {};
    baselineReadyRef.current = new Set();

    // Global connector registry'yi de temizle
    yjsDocument.disposeAll();
  }, [mode]);

  // 🔥 LAZY LOADING: Host receives snapshot request for a file it hasn't opened yet
  useEffect(() => {
    if (!collab.connection || mode !== Mode.Host) return;
    const conn = collab.connection;

    const handleSnapshotRequest = async (sessionIdReq: string, filePath: string, targetConnectionId: string) => {
      console.log(`[Snapshot][Host] Request received | file=${filePath} | from=${targetConnectionId} | session=${sessionIdReq}`);

      try {
        // Ensure we have a connector and local key for this file
        await joinDocument(filePath);

        if (!yjsDocument.keyStore.getLocalKey(collab.sessionId!, filePath)) {
          const identity = await generateIdentityKeyPair();
          yjsDocument.keyStore.ensureLocalKey(
            collab.sessionId!,
            filePath,
            userIdRef.current!,
            identity.ed25519PrivateKey,
            {
              ed25519PublicKey: identity.ed25519PublicKey,
              x25519PrivateKey: identity.x25519PrivateKey,
              x25519PublicKey: identity.x25519PublicKey,
            }
          );
        }

        // ✨ CRITICAL FIX: Send sender key DIRECTLY to the client FIRST
        // This prevents race condition where snapshot arrives before the key announcement
        const local = yjsDocument.keyStore.getLocalKey(collab.sessionId!, filePath);
        if (local && local.rootChainKey && local.ed25519PublicKey) {
          const keyDto = {
            sessionId: collab.sessionId!,
            filePath,
            senderUserId: userIdRef.current!,
            recipientUserId: '',
            senderKeyId: local.senderKeyId,
            nonce: '',
            encryptedSenderRootKey: toBase64(local.rootChainKey), // ✨ Send rootChainKey
            signature: toBase64(local.ed25519PublicKey),
            lastMessageIndex: local.nextMessageIndex - 1,
            x25519PublicKey: local.x25519PublicKey ? toBase64(local.x25519PublicKey) : undefined,
          };
          
          console.log(`[Snapshot][Host] Sending sender key directly to client BEFORE snapshot`);
          await collab.connection!.invoke('SendSenderKeyToUser', targetConnectionId, keyDto);
          console.log(`[Snapshot][Host] ✅ Sender key sent to client`);
        }

        const connector = yjsDocument.getConnector(filePath) as YjsSignalrConnectorE2EE | undefined;
        if (!connector) {
          console.error('[Snapshot][Host] ❌ No connector available to send snapshot');
          return;
        }

        const connState = (connector as any)._client?.state;
        console.log(`[Snapshot][Host] Sending snapshot | connState=${connState}`);
        await connector.sendStateSnapshotToUser(targetConnectionId);
        console.log('[Snapshot][Host] ✅ Snapshot sent');
      } catch (err) {
        console.error('[Snapshot][Host] ❌ Snapshot send failed:', err);
      }
    };

    conn.on('RequestEncryptedStateSnapshotFromHost', handleSnapshotRequest);
    return () => {
      conn.off('RequestEncryptedStateSnapshotFromHost', handleSnapshotRequest);
    };
  }, [collab.connection, mode, collab, announceSenderKeyForFile]);

  // Remote sender key announcements → hydrate keyStore so snapshots/updates can decrypt
  useEffect(() => {
    if (!collab.connection) return;
    const conn = collab.connection;

    const handleSenderKeyAnnouncement = (dto: any) => {
      if (!dto?.sessionId || !dto.filePath || !dto.senderUserId || !dto.senderKeyId || !dto.encryptedSenderRootKey) {
        return;
      }
      
      // ✨ Ignore our own sender key announcement
      if (dto.senderUserId === userIdRef.current) {
        console.log(`[SenderKey] Ignoring own sender key announcement`);
        return;
      }
      
      const chainKey = fromBase64(dto.encryptedSenderRootKey);
      const ed25519PublicKey = dto.signature ? fromBase64(dto.signature) : undefined;
      const x25519PublicKey = dto.x25519PublicKey ? fromBase64(dto.x25519PublicKey) : undefined;
      if (!ed25519PublicKey || ed25519PublicKey.length === 0) {
        console.warn('[SenderKey] Missing ed25519 public key in announcement; ignoring');
        return;
      }

      // ✨ CRITICAL FIX: Check if we already have a more advanced remote key
      // Don't overwrite ratcheted chainKey with rootChainKey from re-announcement
      const existingRemote = yjsDocument.keyStore.getRemoteKey(dto.sessionId, dto.filePath, dto.senderUserId, dto.senderKeyId);
      
      if (existingRemote && existingRemote.lastMessageIndex > -1) {
        // We already have a ratcheted key, don't overwrite with root key
        console.log(`[SenderKey] Remote key already exists with advanced state | file=${dto.filePath} user=${dto.senderUserId} existingIdx=${existingRemote.lastMessageIndex} | Ignoring re-announcement`);
        return;
      }

      const remote: RemoteSenderKeyState & { x25519PublicKey?: Uint8Array; rootChainKey?: Uint8Array } = {
        sessionId: dto.sessionId,
        filePath: dto.filePath,
        senderUserId: dto.senderUserId,
        senderKeyId: dto.senderKeyId,
        chainKey,
        rootChainKey: chainKey, // ✨ Store rootChainKey for snapshot decryption
        lastMessageIndex: typeof dto.lastMessageIndex === 'number' ? dto.lastMessageIndex : -1,
        ed25519PublicKey,
        x25519PublicKey,
      };

      yjsDocument.keyStore.upsertRemoteKey(dto.sessionId, dto.filePath, dto.senderUserId, remote as any);

      console.log(`[SenderKey] Stored remote key | file=${dto.filePath} user=${dto.senderUserId} senderKeyId=${dto.senderKeyId} lastIdx=${remote.lastMessageIndex}`);

      // Nudge connector to flush any buffered updates/snapshots now that key exists
      const connector = yjsDocument.getConnector(dto.filePath) as any;
      connector?._flushAllBufferedUsers?.();
    };

    conn.on('ReceiveSenderKeyAnnouncement', handleSenderKeyAnnouncement);
    return () => {
      conn.off('ReceiveSenderKeyAnnouncement', handleSenderKeyAnnouncement);
    };
  }, [collab.connection, yjsDocument]);

  useEffect(() => {
    openTabs.forEach(tab => {
      if (editorInstancesRef.current[tab.file.path]) {
        editorInstancesRef.current[tab.file.path].tab = tab;
      }
    });

    if (previewTab && editorInstancesRef.current[previewTab.file.path]) {
      editorInstancesRef.current[previewTab.file.path].tab = previewTab;
    }

    if (activeTab && editorInstancesRef.current[activeTab.file.path]) {
      editorInstancesRef.current[activeTab.file.path].tab = activeTab;
    }
  }, [openTabs, previewTab, activeTab]);

  useEffect(() => {
    Object.keys(editorInstancesRef.current).forEach(filePath => {
      rebuildEditorInstance(filePath);
    });
  }, [theme, mode]);

  // Tab kapatılınca ilgili editor instance'ı destroy et
  const handleTabClose = (tab: OpenTab) => {
    if (mode === Mode.Host || mode === Mode.Join) {
      const docData = yDocsRef.current[tab.file.path];
      
      if (mode === Mode.Join) {
        console.log('[EditorContainer] Joiner closing tab visually only - keeping connector/doc alive');
        // Optional: clear local awareness to hide cursor while tab is closed
        if (docData?.awareness) {
          docData.awareness.setLocalState(null);
        }
      } else {
        console.log('[EditorContainer] Host closing tab - KEEPING connector and Y.Doc for background operations');
        
        // HOST: Do NOT destroy connector! It's needed for:
        // 1. Sending snapshots to joiners
        // 2. Maintaining sender key chain continuity
        // 3. Receiving save requests
        
        // Only clear awareness (UI-related)
        if (docData?.awareness) {
          // Remove host's cursor from awareness but keep connector alive
          docData.awareness.setLocalState(null);
        }
        
        // Host keeps:
        // - Y.Doc (for content)
        // - Connector (for encryption/snapshot)
        // - Sender keys (for key continuity)
        // - File-room membership (for save requests)
        console.log('[EditorContainer] Host remains in file-room with active connector');
      }
    }
    // Destroy editor instance
    if (editorInstancesRef.current[tab.file.path]) {
      const ref = editorInstancesRef.current[tab.file.path].ref;
      if (ref && ref.current && typeof ref.current.getDestroy === 'function') {
        const destroyFn = ref.current.getDestroy();
        if (typeof destroyFn === 'function') destroyFn();
      }
      delete editorInstancesRef.current[tab.file.path];
    }
    if (previewTab && previewTab.file.path === tab.file.path) {
      setPreviewTab(null);
      if (activeTab?.file.path === tab.file.path) {
        setActiveTab(null);
      }
      return;
    }
    setOpenTabs(prevTabs => {
      const filtered = prevTabs.filter(t => t.file.path !== tab.file.path);
      let nextActive: OpenTab | null = null;
      if (activeTab?.file.path === tab.file.path) {
        if (filtered.length > 0) {
          nextActive = filtered[filtered.length - 1];
          setActiveTab(nextActive);
          setLanguage(getLanguageFromExtension(nextActive.file.name));
        } else if (previewTab) {
          nextActive = previewTab;
          setActiveTab(previewTab);
          setLanguage(getLanguageFromExtension(previewTab.file.name));
        } else {
          setActiveTab(null);
        }
      }
      return filtered;
    });
  };

  // Tab içeriğini openDocuments ile güncelle
  function getTabContent(tab: OpenTab) {
    if (mode === Mode.Host || mode === Mode.Join) {
      // Yjs dokümanından oku
      const ydoc = getYDocForFile(tab.file.path).ydoc;
      return ydoc.getText('monaco').toString();
    }
    return tab.content;
  }

  // Dosya açma/önizleme işlemleri
  const handleFileClick = async (file: FileItem, options?: { preview?: boolean }) => {
    const isPermanent = openTabs.some(tab => tab.file.path === file.path);
    const isPreview = previewTab && previewTab.file.path === file.path;

    // Eğer zaten permanent tab varsa, sadece aktif yap
    if (isPermanent) {
      const tab = openTabs.find(tab => tab.file.path === file.path)!;
      setActiveTab(tab);
      setLanguage(getLanguageFromExtension(file.name));
      if (mode === Mode.Host || mode === Mode.Join) {
        if (!joinedFilesRef.current.has(file.path)) {
          joinDocument(file.path);
        } else {
          // Sadece aktif belge bilgisini güncelle (join/leave yapma)
          collab.connection?.invoke('SetActiveDocument', collab.sessionId, file.path, username);
        }
      }
      return;
    }

    // Preview tab'dan permanent'a geçiş
    if (options?.preview === false && isPreview && previewTab) {
      setOpenTabs(prevTabs => {
        if (prevTabs.find(tab => tab.file.path === file.path)) return prevTabs;
        return [...prevTabs, previewTab];
      });
      setPreviewTab(null);
      setActiveTab(previewTab);
      setLanguage(getLanguageFromExtension(file.name));
      
      // 📌 Ensure join is called (will be skipped if already joined)
      if (mode === Mode.Host || mode === Mode.Join) {
        joinDocument(file.path);
      }
      return;
    }

    // Session mode: Yjs document ile çalış
    if (mode === Mode.Host || mode === Mode.Join) {
      try {
        // İlk olarak Yjs doc oluştur
        const { ydoc } = getYDocForFile(file.path);

        const newTab: OpenTab = {
          file,
          content: '', // Başlangıçta boş, joinDocument'ten sonra gelecek
          originalContent: '',
          isDirty: false
        };

        // Tab'ı ayarla
        setActiveTab(newTab);
        setLanguage(getLanguageFromExtension(file.name));

        if (options?.preview === false) {
          // Permanent tab
          setOpenTabs(prevTabs => {
            if (prevTabs.find(tab => tab.file.path === file.path)) return prevTabs;
            return [...prevTabs, newTab];
          });
          setPreviewTab(null);
        } else {
          // Preview tab
          setPreviewTab(newTab);
        }

        // Editor instance'ı oluştur
        ensureEditorInstance(newTab);

        // Son olarak joinDocument çağır - bu içeriği yükleyecek
        joinDocument(file.path);
      } catch (error) {
        console.error('Error opening file in session mode:', error);
      }
      return;
    }

    // Local mod: dosyayı disk'ten oku
    try {
      const content = await fileSystemService.readFile(file.path);
      const newTab: OpenTab = {
        file,
        content,
        originalContent: content,
        isDirty: false
      };

      setActiveTab(newTab);
      setLanguage(getLanguageFromExtension(file.name));

      if (options?.preview === false) {
        // Permanent tab
        setOpenTabs(prevTabs => {
          if (prevTabs.find(tab => tab.file.path === file.path)) return prevTabs;
          return [...prevTabs, newTab];
        });
        setPreviewTab(null);
      } else {
        // Preview tab
        setPreviewTab(newTab);
      }

      console.log('Opened file:', file.path);
      // Editor instance'ı oluştur
      ensureEditorInstance(newTab);
    } catch (error) {
      console.error('Error reading file:', error);
      // TODO: Show error notification to user
    }
  };

  const handleTabClick = async (tab: OpenTab) => {
    if (activeTab?.file.path === tab.file.path) return;
    if (mode === Mode.Host || mode === Mode.Join) {
      if (!joinedFilesRef.current.has(tab.file.path)) {
        joinDocument(tab.file.path);
      } else {
        collab.connection?.invoke('SetActiveDocument', collab.sessionId, tab.file.path, username);
      }
    }
    setActiveTab(tab);
    // Preview tab'a tıklanınca onu temizleme - aktif olarak kalmalı
    setLanguage(getLanguageFromExtension(tab.file.name));
  };

  const handleMakePermanent = (tab: OpenTab) => {
    // Permanent tab'lara ekle ve preview'ı temizle
    setOpenTabs(prevTabs => {
      // Zaten varsa hiçbir şey yapma
      if (prevTabs.find(t => t.file.path === tab.file.path)) return prevTabs;
      return [...prevTabs, tab];
    });

    // Preview tab'ı temizle
    setPreviewTab(null);
  };

  // SignalR event handlers for user join/leave notifications
  useEffect(() => {
    if (!collab.connection) return;

    const onUserJoinedDocument = (userId: string, userName: string, color: string, filePath: string) => {
      // If host isn't joined yet but a client joined first, silently join to become authority
      if (mode === Mode.Host && !joinedFilesRef.current.has(filePath)) {
        console.log(`[Host][AutoJoin] User ${userName} joined ${filePath}, host auto-joining to own the room`);
        joinDocument(filePath).catch(err => console.error('[Host][AutoJoin] Failed:', err));
      }

      // If we are a joiner waiting for snapshot and host just appeared, re-request snapshot
      if (mode === Mode.Join && joinedFilesRef.current.has(filePath)) {
        const docData = yDocsRef.current[filePath];
        const conn = docData?.connector as any;
        if (conn && docData?.snapshotReady !== true) {
          console.log(`[Joiner][ReSnapshot] ${userName} joined ${filePath}, forcing snapshot request`);
          conn.requestSnapshot?.();
        }
      }

      // ✨ NOTE: We do NOT re-announce our sender key here!
      // Re-announcing rootChainKey would override other users' ratcheted chainKeys
      // The new user will get our sender key via:
      // 1. SendSenderKeyToUser when we send them a snapshot (if we're host)
      // 2. Our initial announcement when we joined (they'll buffer updates until they get it)
      console.log(`[UserJoined] ${userName} joined ${filePath} | No sender key re-announcement needed`);
    };

    const onUserLeftDocument = (userId: string, userName: string, filePath: string) => {
      // User left document
      console.log(`[EditorContainer] User left document: ${userName} | file: ${filePath}`);
    };

    collab.connection.on('UserJoinedDocument', onUserJoinedDocument);
    collab.connection.on('UserLeftDocument', onUserLeftDocument);

    return () => {
      collab.connection?.off('UserJoinedDocument', onUserJoinedDocument);
      collab.connection?.off('UserLeftDocument', onUserLeftDocument);
    };
  }, [collab.connection, mode, announceSenderKeyForFile]);

  useEffect(() => {
    if (!collab.connection) return;

    const onDocumentDirtyChanged = (filePath: string, isDirty: boolean) => {
      setTabDirty(filePath, isDirty, { updateOriginal: !isDirty });
      if (!isDirty) {
        baselineReadyRef.current.add(filePath);
      }
    };

    const onHandleSaveRequest = async (_sessionId: string, filePath: string) => {
      if (mode !== Mode.Host) return;
      
      // CRITICAL: Only save if host still has this file open
      // If host closed the tab, Y.Doc was disposed - don't create empty one!
      if (!joinedFilesRef.current.has(filePath)) {
        console.warn(`[Host] Ignoring save request for closed file: ${filePath}`);
        return;
      }
      
      const docData = yDocsRef.current[filePath];
      if (!docData || !docData.ydoc) {
        console.warn(`[Host] Ignoring save request - no Y.Doc for: ${filePath}`);
        return;
      }
      
      const targetTab = openTabsRef.current.find(t => t.file.path === filePath) || activeTabRef.current;
      await handleSave(targetTab || undefined, filePath);
    };

    collab.connection.on('DocumentDirtyChanged', onDocumentDirtyChanged);
    collab.connection.on('HandleSaveRequest', onHandleSaveRequest);

    const onSaveRequestFailed = (filePath: string, reason: string) => {
      console.error(`[EditorContainer] Save failed for ${filePath}: ${reason}`);
      // TODO: Show error notification to user
      alert(`Save failed: ${reason}`);
    };
    
    collab.connection.on('SaveRequestFailed', onSaveRequestFailed);

    return () => {
      collab.connection?.off('DocumentDirtyChanged', onDocumentDirtyChanged);
      collab.connection?.off('HandleSaveRequest', onHandleSaveRequest);
      collab.connection?.off('SaveRequestFailed', onSaveRequestFailed);
    };
  }, [collab.connection, mode]);

  useEffect(() => {
    if (!username) return;
    const userColor = collab.users.find(u => u.name === username)?.color;
    if (!userColor) return;

    Object.values(yDocsRef.current).forEach(docData => {
      docData.connector?.setLocalUserState(username, userColor);
    });
  }, [collab.users, username]);

  // Editor change handler
  const handleEditorChange = (value: string | undefined) => {
    // Allow empty string so blank files still track dirty state
    if (value === undefined || !activeTab) return;

    // In collaborative mode, Y.Doc is updated automatically by Monaco binding
    // This handler only updates local tab state
    const isCollaborative = mode === Mode.Host || mode === Mode.Join;

    // Preview tab'da GERÇEK değişiklik yapılırsa otomatik permanent yap
    // Sadece içerik gerçekten değiştiyse (ilk yükleme veya cursor hareketi değil)
    if (previewTab && previewTab.file.path === activeTab.file.path) {
      // İçerik değişmediyse hiçbir şey yapma
      if (value === activeTab.content) return;

      setOpenTabs(prevTabs => {
        if (prevTabs.find(tab => tab.file.path === activeTab.file.path)) return prevTabs;
        return [...prevTabs, { ...previewTab, content: value, isDirty: value !== previewTab.originalContent }];
      });
      setPreviewTab(null);
      setActiveTab({ ...activeTab, content: value, isDirty: value !== activeTab.originalContent });
      return;
    }

    // In collaborative mode, skip updating tab.content - read from Y.Doc instead
    if (isCollaborative) {
      // Y.Doc is the source of truth, tab content will be read via getTabContent
      return;
    }

    // Normal tab update (LOCAL MODE ONLY)
    setOpenTabs(prevTabs => prevTabs.map(tab =>
      tab.file.path === activeTab.file.path
        ? { ...tab, content: value, isDirty: value !== tab.originalContent }
        : tab
    ));
    setActiveTab((tab: OpenTab | null) =>
      tab && tab.file.path === activeTab.file.path
        ? { ...tab, content: value, isDirty: value !== tab.originalContent }
        : tab
    );
  };

  function createEditorElement(tab: OpenTab, existingRef?: React.RefObject<any>) {
    const filePath = tab.file.path;
    console.log('🎬 [createEditorElement] Called for file:', filePath);
    const fileDoc = getYDocForFile(filePath);
    const isCollaborative = mode === Mode.Host || mode === Mode.Join;
    const ref = existingRef ?? React.createRef<any>();

    console.log('📋 [createEditorElement] Mode:', mode, '| Collaborative:', isCollaborative);
    console.log('📄 [createEditorElement] Y.Doc exists:', !!fileDoc.ydoc, '| Awareness exists:', !!fileDoc.awareness);
    if (fileDoc.ydoc) {
      console.log('🆔 [createEditorElement] Y.Doc clientID:', fileDoc.ydoc.clientID);
    }

    // If collaborative and snapshot not ready yet, show loading spinner
    if (isCollaborative && fileDoc.snapshotReady === false) {
      console.log('⏳ [createEditorElement] Waiting for snapshot...');
      const loadingNode = (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: theme === 'vs-dark' ? '#fff' : '#000',
          fontSize: '14px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '10px' }}>⏳</div>
            <div>Loading document...</div>
          </div>
        </div>
      );
      return { editor: loadingNode, ref };
    }

    const editorNode = (
      <Editor
        key={`${filePath}-${isCollaborative ? 'collab' : 'local'}-${theme}`}
        code={getTabContent(tab)}
        language={getLanguageFromExtension(tab.file.name)}
        theme={theme}
        onChange={handleEditorChange}
        ydoc={isCollaborative ? fileDoc.ydoc : undefined}
        awareness={isCollaborative ? fileDoc.awareness : undefined}
        collaborative={isCollaborative}
        onSave={() => handleSave(tab)}
        ref={ref}
      />
    );

    return { editor: editorNode, ref };
  }

  function rebuildEditorInstance(filePath: string) {
    console.log('🔨 [rebuildEditorInstance] Called for file:', filePath);
    const existing = editorInstancesRef.current[filePath];
    if (!existing) {
      console.log('  ❌ No existing editor instance found');
      return;
    }

    console.log('  ✅ Existing editor found, recreating...');
    console.log('  📊 Y.Doc exists:', !!yDocsRef.current[filePath]?.ydoc);
    console.log('  📊 Awareness exists:', !!yDocsRef.current[filePath]?.awareness);
    console.log('  📊 Connector exists:', !!yDocsRef.current[filePath]?.connector);
    
    const { editor, ref } = createEditorElement(existing.tab, existing.ref);
    editorInstancesRef.current[filePath] = {
      editor,
      ref,
      tab: existing.tab
    };
    console.log('  ✅ Editor instance updated, forcing rerender');
    forceEditorsRerender(prev => prev + 1);
  }

  const handleSave = async (tab?: OpenTab, filePathOverride?: string) => {
    const targetPath = filePathOverride
      || tab?.file.path
      || activeTabRef.current?.file.path
      || activeTab?.file.path;

    if (!targetPath) return;

    if (mode === Mode.Host) {
      const { ydoc } = getYDocForFile(targetPath);
      const yContent = ydoc.getText('monaco').toString();
      const realPath = collab.getRealPath(targetPath) || targetPath;
      try {
        await fileSystemService.writeFile(realPath, yContent);
        setTabDirty(targetPath, false, { updateOriginal: true });
        baselineReadyRef.current.add(targetPath);
        if (collab.sessionId && collab.connection) {
          await collab.connection.invoke('RequestSave', collab.sessionId, targetPath);
        }
      } catch (error) {
        console.error('Error saving file:', error);
      }
      return;
    }

    if (mode === Mode.Join) {
      const isDirty = dirtyStateRef.current[targetPath] ?? openTabsRef.current.find(t => t.file.path === targetPath)?.isDirty;
      if (!isDirty) return;
      if (collab.sessionId && collab.connection) {
        try {
          await collab.connection.invoke('RequestSave', collab.sessionId, targetPath);
        } catch (error) {
          console.error('Error requesting host save:', error);
        }
      }
      return;
    }

    // Local mode
    const targetTab = openTabsRef.current.find(t => t.file.path === targetPath)
      || activeTabRef.current
      || tab
      || activeTab;
    if (!targetTab || !targetTab.isDirty) return;
    try {
      await fileSystemService.writeFile(targetTab.file.path, targetTab.content);
      setTabDirty(targetPath, false, { updateOriginal: true, originalContentOverride: targetTab.content });
    } catch (error) {
      console.error('Error saving file:', error);
    }
  };



  const handleFileRenamed = (oldFile: FileItem, newFile: FileItem) => {
    let newTabs: OpenTab[] = [];
    setOpenTabs(prevTabs => {
      newTabs = prevTabs.map((tab: OpenTab) => {
        if (tab.file.path === oldFile.path) {
          return { ...tab, file: newFile };
        }
        return tab;
      });
      return newTabs;
    });
    if (activeTab?.file.path === oldFile.path) {
      setActiveTab(newTabs.find((tab: OpenTab) => tab.file.path === newFile.path) || null);
    }
  };

  // Tab yeniden sıralama handler'ı
  const handleTabsReorder = (newOrderedTabs: OpenTab[]) => {
    // EditorTabs'dan gelen liste zaten preview tab olmadan geliyor
    setOpenTabs(newOrderedTabs);
  };

  // Yjs dokümanının içeriği yüklendi mi?
  const isYDocReady = React.useMemo(() => {
    if (!(mode === Mode.Host || mode === Mode.Join) || !activeTab) return true;
    const filePath = activeTab.file.path;
    const docData = yDocsRef.current[filePath];
    // Connector ve awareness varsa ready (boş dosyalar için de geçerli)
    if (docData && docData.connector && docData.awareness) {
      return true;
    }
    // Editor instance varsa da ready sayabiliriz
    if (editorInstancesRef.current[filePath]) {
      return true;
    }
    return false;
  }, [mode, activeTab, editorInstancesRef.current]);

  return (
    <div className="editor-root-layout" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: '0 0 auto', zIndex: 2 }}>
        <TitleBarContainer
          onSettingsVisibilityChange={setSettingsVisible}
          onSave={() => handleSave()}
          onLoginClick={() => setIsLoginModalOpen(true)}
          onRegisterClick={() => setIsRegisterModalOpen(true)}
        />
      </div>
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ width: 250, flexShrink: 0, background: '#23262F', borderRight: '1px solid #23262F', minWidth: 200 }}>
          <SidebarContainer
            onFileClick={(file, options) => handleFileClick(file, options)}
            isSidebarVisible={true}
            onToggleSidebar={() => { }}
            onFileDeleted={(file) => {
              const tab = openTabs.find(tab => tab.file.path === file.path);
              if (tab) handleTabClose(tab);
            }}
            onFileRenamed={handleFileRenamed}
            previewTab={previewTab ? previewTab.file : null}
          />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <EditorTabs
            tabs={openTabs}
            previewTab={previewTab}
            activeFile={activeTab}
            onTabClick={handleTabClick}
            onTabClose={handleTabClose}
            onMakePermanent={handleMakePermanent}
            onTabsReorder={handleTabsReorder}
          />
          <div style={{ flex: 1, minHeight: 0 }}>
            {(!activeTab || (mode === Mode.Host || mode === Mode.Join) && !isYDocReady) ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <span>Loading document...</span>
              </div>
            ) : (
              activeTab && editorInstancesRef.current[activeTab.file.path]?.editor
            )}
          </div>
          <BottomPanel
            isVisible={isBottomPanelVisible}
            onToggleVisibility={() => setBottomPanelVisible(!isBottomPanelVisible)}
          />
        </div>
      </div>
      <Settings
        isVisible={isSettingsVisible}
        onClose={() => setSettingsVisible(false)}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSwitchToRegister={() => {
          setIsLoginModalOpen(false);
          setIsRegisterModalOpen(true);
        }}
      />
      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSwitchToLogin={() => {
          setIsRegisterModalOpen(false);
          setIsLoginModalOpen(true);
        }}
      />
    </div>
  );
};

export default EditorContainer;


